import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Calendar, Warehouse, Users, ShieldCheck, ClipboardList, BarChart3, Rocket } from "lucide-react";

const ORGS = ["Tropicana", "Walmart", "Kennedy Space Center", "Blue Origin"];

const PHASES = [
  {
    n: "01", title: "Foundation", days: "Days 1 – 30", color: "#F7931E", tone: "orange",
    icon: ClipboardList,
    items: ["Mindset & Business Blueprint", "Legal, Insurance & Set Up", "Equipment, Branding & Systems"],
  },
  {
    n: "02", title: "Build", days: "Days 31 – 60", color: "#168CFF", tone: "blue",
    icon: BarChart3,
    items: ["Marketing That Books", "Sales, Pricing & Packages", "Logistics & Event Execution"],
  },
  {
    n: "03", title: "Scale", days: "Days 61 – 90", color: "#78C800", tone: "green",
    icon: Rocket,
    items: ["Hiring & Delegation", "Scaling Revenue & Profit", "Building a Brand That Lasts"],
  },
];

const CRED = [
  { icon: ShieldCheck, top: "13+", label: "Years of Industry\nExperience" },
  { icon: Calendar,   top: "12",  label: "Week\nProgram" },
  { icon: Warehouse,  top: "Live", label: "Warehouse\nTraining" },
  { icon: Users,      top: "15",  label: "Limited to\nStudents" },
];

const TEAM_POINTS = [
  "Live warehouse training",
  "Done-with-you guidance",
  "A team that actually cares",
  "Support that continues beyond 90 days",
];

const OPERATIONS_POINTS = [
  "Real Operations",
  "Recognized Organizations Served",
  "Hands-On Business Exposure",
];

export default function Home() {
  useEffect(() => {
    // Accessibility: pause and hide the hero video for visitors who prefer reduced motion.
    // The still-image backdrop stays visible so the hero never goes blank.
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      const v = document.querySelector('video[data-testid="hero-video"]');
      if (!v) return;
      if (mql.matches) {
        try { v.pause(); } catch { /* ignore */ }
        v.style.display = "none";
      } else {
        v.style.removeProperty("display");
      }
    };
    apply();
    mql.addEventListener?.("change", apply);
    return () => mql.removeEventListener?.("change", apply);
  }, []);
  return (
    <div data-testid="home-page" className="bg-black text-white">
      {/* 1 · HERO — full-width cinematic with event background + Emmanuel right */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        {/* Event background — still image is the persistent backdrop.
             On desktop with motion allowed, the brand video plays on top of it.
             If the video can't autoplay or the visitor prefers reduced motion, the still remains visible. */}
        <div className="absolute inset-0">
          <img
            src="/assets/07_event_setup.jpg"
            alt="Authentic Bounce City inflatable event setup"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <video
            data-testid="hero-video"
            className="hero-video absolute inset-0 w-full h-full object-cover object-center hidden md:block"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/assets/07_event_setup.jpg"
            aria-hidden="true"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          >
            <source src="/assets/hero_video_720p.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-black" />
        </div>

        {/* Hero content */}
        <div className="bcu-container-wide relative z-10 pt-28 pb-16 md:pt-32 md:pb-20">
          <div className="max-w-[720px]">
            <p className="bcu-eyebrow bcu-eyebrow-gold">Bounce City University</p>
            <h1 className="mt-6 font-display text-white text-5xl sm:text-6xl lg:text-[86px] leading-[0.98] font-semibold">
              <span className="block">Build a Profitable</span>
              <span className="block text-[#F7931E] my-1">Inflatable Rental<br />Business</span>
              <span className="block">in 90 Days.</span>
            </h1>
            <p className="mt-8 font-editorial italic text-[#D9A441] text-2xl md:text-[28px] leading-snug max-w-[600px]">
              Real operating experience. Practical systems. A clear path forward.
            </p>
            <p className="mt-6 text-white/90 text-[17px] md:text-[18px] leading-[1.65] max-w-[600px]">
              A hands-on, done-with-you mentorship program for serious entrepreneurs ready to structure, launch, operate, market and scale a professional inflatable rental company.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/apply" data-testid="hero-apply-btn" className="bcu-btn-primary text-[14px] tracking-[0.10em] uppercase">Apply for one of 15 seats <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/blueprint" data-testid="hero-blueprint-btn" className="bcu-btn-secondary text-[14px] tracking-[0.10em] uppercase">Explore the 90-day blueprint</Link>
            </div>
            <p className="mt-5 text-sm text-[#858585]">Applications are reviewed before enrollment. Acceptance is not guaranteed.</p>

            {/* Credibility pill strip — matches mockup */}
            <div className="mt-10 inline-flex flex-wrap items-stretch gap-0 border border-[rgba(217,164,65,0.35)] rounded-lg bg-black/70 backdrop-blur-sm">
              {CRED.map((c, i) => (
                <div key={i} className={`flex items-center gap-3 px-5 py-4 ${i < CRED.length - 1 ? "border-r border-[rgba(217,164,65,0.20)]" : ""}`}>
                  <c.icon className="h-6 w-6 text-[#F7931E] flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="font-display text-xl md:text-2xl text-white leading-none">{c.top}</div>
                    <div className="mt-1 text-[10.5px] tracking-[0.08em] uppercase text-[#B5B5B5] leading-tight whitespace-pre-line">{c.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2 · MEET EMMANUEL — editorial founder intro with signature */}
      <section className="sect-black py-20 md:py-28">
        <div className="bcu-container grid lg:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="lg:col-span-4">
            <div className="relative">
              <img src="/assets/01_emmanuel_portrait.jpg" alt="Emmanuel Ternier — Founder, Bounce City" className="w-full max-h-[560px] object-cover object-top rounded-md border border-[rgba(217,164,65,0.24)]" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <p className="bcu-eyebrow bcu-eyebrow-gold">From Vision to Legacy</p>
            <h2 className="mt-5 font-display text-white text-4xl md:text-5xl lg:text-[54px] leading-[1.04] font-medium">Meet Emmanuel Ternier</h2>
            <div className="mt-4 text-[13px] tracking-[0.18em] uppercase text-[#F7931E] font-semibold">
              Founder &amp; CEO of Bounce City 321<br />
              Founder of Bounce City University
            </div>
            <div className="mt-8 space-y-5 text-[#B5B5B5] text-[17px] leading-[1.75] max-w-[640px]">
              <p>For more than 13 years, Emmanuel Ternier has built, operated and expanded Bounce City 321, an established inflatable rental company serving communities and organizations across Central Florida.</p>
              <p>His operating experience includes business development, logistics, customer service, staffing, marketing, safety and strategic growth. He created Bounce City University to turn those real operating lessons into a structured roadmap for entrepreneurs who want to build professionally.</p>
            </div>

            <blockquote className="mt-10 pl-6 border-l-2 border-[#D9A441] max-w-[640px]">
              <p className="pull-quote">Real operating experience. Practical systems. Hands-on guidance.</p>
              <footer className="mt-5 font-editorial italic text-[#D9A441] text-lg">— Bounce City University</footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* 3 · BUILT IN THE FIELD — real operations photos */}
      <section className="sect-black-2 py-20 md:py-24 border-y border-[rgba(217,164,65,0.15)]">
        <div className="bcu-container">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 grid grid-cols-2 gap-4">
              <figure className="group relative rounded-md overflow-hidden border border-[rgba(255,255,255,0.10)] bg-[#0A0A0A]">
                <div className="relative overflow-hidden">
                  <img src="/assets/03_truck_tropicana.jpg" alt="Bounce City branded truck near a Tropicana location in Central Florida" className="w-full h-56 md:h-72 object-cover object-center transition-transform duration-500 md:group-hover:scale-[1.03]" />
                  <figcaption className="hidden md:block absolute inset-x-0 bottom-0 px-5 pt-14 pb-4 bg-gradient-to-t from-black/95 via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-body text-[13px] text-white leading-snug">Real Bounce City delivery operations across Central Florida.</figcaption>
                </div>
                <figcaption className="md:hidden px-4 py-3 text-[13px] text-[#B5B5B5] font-body">Real Bounce City delivery operations across Central Florida.</figcaption>
              </figure>
              <figure className="group relative rounded-md overflow-hidden border border-[rgba(255,255,255,0.10)] bg-[#0A0A0A]">
                <div className="relative overflow-hidden">
                  <img src="/assets/04_trailer_palmbay.jpg" alt="Bounce City branded trailer at Palm Bay Hospital" className="w-full h-56 md:h-72 object-cover object-center transition-transform duration-500 md:group-hover:scale-[1.03]" />
                  <figcaption className="hidden md:block absolute inset-x-0 bottom-0 px-5 pt-14 pb-4 bg-gradient-to-t from-black/95 via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-body text-[13px] text-white leading-snug">Professional event logistics and branded fleet operations.</figcaption>
                </div>
                <figcaption className="md:hidden px-4 py-3 text-[13px] text-[#B5B5B5] font-body">Professional event logistics and branded fleet operations.</figcaption>
              </figure>
            </div>
            <div className="lg:col-span-5">
              <p className="bcu-eyebrow">Real World. Real Operations.</p>
              <h2 className="mt-5 font-display text-white text-4xl md:text-5xl leading-[1.04] font-medium">Built in the Field.<br /><span className="text-[#D9A441] font-editorial italic font-medium">Proven in the Real World.</span></h2>
              <ul className="mt-8 space-y-4">
                {OPERATIONS_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-white text-[17px]">
                    <span className="check-circle mt-0.5 border-[#F7931E] text-[#F7931E]"><Check className="h-3 w-3" strokeWidth={3} /></span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link to="/hands-on-training" data-testid="ops-cta" className="mt-8 inline-flex bcu-btn-secondary text-[13px] tracking-[0.14em] uppercase">See our operations <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 4 · 90-DAY ROADMAP — three-phase band */}
      <section className="sect-black py-20 md:py-24">
        <div className="bcu-container text-center mb-12">
          <p className="bcu-eyebrow bcu-eyebrow-gold justify-center" style={{ display: "inline-flex" }}>The 90-Day Roadmap</p>
          <h2 className="mt-5 font-display text-white text-4xl md:text-5xl lg:text-[56px] leading-[1.04] font-medium">Three Phases. <span className="font-editorial italic font-medium text-[#D9A441]">One Clear Path.</span></h2>
        </div>
        <div className="bcu-container grid md:grid-cols-3 gap-4">
          {PHASES.map((p) => {
            const bgGrad = p.tone === "orange"
              ? "bg-gradient-to-br from-[#3A1E00] via-[#2A1600] to-[#150B00]"
              : p.tone === "blue"
              ? "bg-gradient-to-br from-[#0A2244] via-[#081A33] to-[#040C1B]"
              : "bg-gradient-to-br from-[#1F3800] via-[#152800] to-[#0A1400]";
            const Icon = p.icon;
            return (
              <Link to="/blueprint" key={p.n} data-testid={`phase-${p.n}`} className={`relative ${bgGrad} rounded-md p-8 border transition-transform duration-300 hover:-translate-y-1`} style={{ borderColor: p.color + "44" }}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="numeral-xl" style={{ color: p.color }}>{p.n}</div>
                  </div>
                  <Icon className="h-10 w-10 opacity-80" style={{ color: p.color }} strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-display text-3xl md:text-4xl text-white font-medium tracking-[0.02em] uppercase">{p.title}</h3>
                <div className="mt-1 text-[13px] tracking-[0.18em] uppercase font-semibold" style={{ color: p.color }}>{p.days}</div>
                <ul className="mt-6 space-y-2.5">
                  {p.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-white/90 text-[15px]">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: p.color }} />
                      {it}
                    </li>
                  ))}
                </ul>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 5 · YOU'RE NEVER BUILDING ALONE — team */}
      <section className="sect-black-2 py-20 md:py-24">
        <div className="bcu-container grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="rounded-md overflow-hidden border border-[rgba(255,255,255,0.10)]">
              <img src="/assets/05_team.jpg" alt="Bounce City operating team" className="w-full h-72 md:h-[460px] object-cover object-center" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="bcu-eyebrow">Hands-On Training. Real Support.</p>
            <h2 className="mt-5 font-display text-white text-4xl md:text-5xl leading-[1.04] font-medium">You're Never Building <span className="font-editorial italic font-medium text-[#F7931E]">Alone.</span></h2>
            <p className="mt-6 text-[#B5B5B5] text-[17px] leading-[1.75] max-w-md">Bounce City University combines structured instruction with implementation guidance and exposure to a real operating team.</p>
            <ul className="mt-8 space-y-4">
              {TEAM_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-white text-[17px]">
                  <span className="check-circle mt-0.5 border-[#F7931E] text-[#F7931E]"><Check className="h-3 w-3" strokeWidth={3} /></span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <Link to="/about" data-testid="team-cta" className="mt-8 inline-flex bcu-btn-secondary text-[13px] tracking-[0.14em] uppercase">Meet the team <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* 6 · BUILT FOR IMPACT — community */}
      <section className="sect-black py-20 md:py-24">
        <div className="bcu-container grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <p className="bcu-eyebrow">Leadership. Community. Impact.</p>
            <h2 className="mt-5 font-display text-white text-4xl md:text-5xl leading-[1.04] font-medium">Built for Impact.<br /><span className="font-editorial italic font-medium text-[#D9A441]">Driven by Community.</span></h2>
            <div className="mt-8 space-y-5 text-[#B5B5B5] text-[17px] leading-[1.75] max-w-md">
              <p>Emmanuel's work extends beyond business operations. Under his leadership, Bounce City has supported community events, charitable initiatives and family celebrations across Central Florida.</p>
              <p>Bounce City has also served as a major sponsor of the Tax City Toy Drive for nearly eight years, and Emmanuel has received recognition relating to community service and business leadership from the cities of West Melbourne, Palm Bay and Melbourne.</p>
            </div>
            <Link to="/about" data-testid="community-cta" className="mt-8 inline-flex bcu-btn-secondary text-[13px] tracking-[0.14em] uppercase">Join the movement <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="lg:col-span-7">
            <figure className="group relative rounded-md overflow-hidden border border-[rgba(255,255,255,0.10)]">
              <div className="relative overflow-hidden">
                <img src="/assets/06_community.jpg" alt="A community event supported by Bounce City" className="w-full h-72 md:h-[500px] object-cover object-top transition-transform duration-500 md:group-hover:scale-[1.03]" />
                <figcaption className="hidden md:block absolute inset-x-0 bottom-0 px-6 pt-16 pb-5 bg-gradient-to-t from-black/95 via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-body text-[14px] text-white leading-snug">Community leadership and local event involvement.</figcaption>
              </div>
              <figcaption className="md:hidden px-4 py-3 text-[13px] text-[#B5B5B5] font-body">Community leadership and local event involvement.</figcaption>
            </figure>
            <p className="mt-3 text-xs text-[#858585] italic">A local community event supported by Bounce City. Persons featured are pictured for community context.</p>
          </div>
        </div>
      </section>

      {/* 7 · EXPERIENCE SERVING RECOGNIZED ORGANIZATIONS */}
      <section className="sect-black-2 py-16 md:py-20 border-y border-[rgba(217,164,65,0.15)]">
        <div className="bcu-container text-center">
          <p className="bcu-eyebrow bcu-eyebrow-gold justify-center" style={{ display: "inline-flex" }}>Experience Serving Recognized Organizations</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-14 gap-y-6">
            {ORGS.map((o) => (
              <div key={o} className="font-display text-2xl md:text-3xl text-white/90 tracking-[0.05em]">{o}</div>
            ))}
          </div>
            <p className="mt-8 max-w-3xl mx-auto text-xs text-[#858585] italic leading-relaxed">Bounce City has provided services for or worked with organizations including Tropicana, Walmart, Kennedy Space Center and Blue Origin. Organization names are referenced only to describe prior service relationships. No endorsement is implied.</p>
        </div>
      </section>

      {/* 8 · INVESTMENT CTA — matches mockup */}
      <section className="sect-black py-20 md:py-24">
        <div className="bcu-container">
          <div className="relative border-y-2 border-[#D9A441]/70 py-14 md:py-16">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-5">
                <p className="text-[13px] tracking-[0.24em] uppercase text-[#D9A441] font-semibold">Invest in Your Future</p>
                <div className="mt-4 flex flex-col gap-2">
                  <span className="inline-flex items-center px-3 py-1 border border-[#F7931E]/40 bg-[#F7931E]/10 text-[#F7931E] text-[10px] tracking-[0.24em] uppercase font-semibold rounded self-start">Limited-Time Offer</span>
                  <div className="mt-2">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-[#7A7A7A] font-semibold">Regular Investment</div>
                    <div className="mt-1 font-display text-2xl md:text-3xl text-[#7A7A7A] line-through opacity-70">$10,000</div>
                  </div>
                  <div className="mt-4">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-[#F7931E] font-semibold">Limited-Time Enrollment</div>
                    <div className="mt-1 font-display text-6xl md:text-7xl lg:text-[92px] text-white leading-none font-semibold">$5,000</div>
                  </div>
                </div>
                <div className="mt-3 text-[12px] tracking-[0.28em] uppercase text-[#B5B5B5] font-semibold">Program Investment</div>
              </div>
              <div className="lg:col-span-7 lg:pl-10 lg:border-l border-[#D9A441]/40">
                <p className="text-white/90 text-lg md:text-xl leading-relaxed max-w-lg">Spots are limited to 15 committed entrepreneurs.</p>
                <h3 className="mt-4 font-display text-3xl md:text-4xl text-white leading-tight font-medium">Apply for one of 15 seats.</h3>
                <p className="mt-3 text-[#B5B5B5] text-[16px]">Begin building with a clearer roadmap, practical systems and real operating guidance.</p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Link to="/apply" data-testid="investment-apply-btn" className="bcu-btn-primary text-[14px] tracking-[0.14em] uppercase">Apply now <ArrowRight className="h-4 w-4" /></Link>
                  <Link to="/blueprint" className="bcu-btn-secondary text-[14px] tracking-[0.14em] uppercase">See the program</Link>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-6 max-w-4xl text-xs text-[#858585] leading-relaxed">Equipment, insurance, licenses, travel, accommodation, advertising, software and other business expenses are separate unless stated in the signed agreement.</p>
        </div>
      </section>
    </div>
  );
}
